import {
	DhcClientResult,
	DhcAdminClientResult,
	DhcOpenUsersResult,
	DhcDetails,
	DhcExpertiseResult,
	DhcListResult,
	DhcPayouts,
	DhcPayoutApprovalResult,
	DhcQaResult,
	DhcResult,
	ConfigureDhcPayload,
	DhcNotesResult,
} from "@/modules/dhcs/types";
import { axiosInstance } from "../common";

const baseUrl = "aggregator/v1/delivery/dhc";

export const getDhcs = async (filter: Record<string, string | number | boolean>): Promise<DhcResult> => {
	const page = filter?.page ? filter.page : 1;
	const limit = filter?.limit ? filter.limit : 10;

	const { dhcKeywords, clientKeywords, status, keywords } = filter as {
		dhcKeywords?: string;
		clientKeywords?: string;
		status?: string;
		keywords?: string;
	};

	const params: Record<string, string | number | boolean> = { page, limit };
	if (dhcKeywords) {
		params.dhcKeywords = dhcKeywords;
	}
	if (clientKeywords) {
		params.clientKeywords = clientKeywords;
	}
	if (status) {
		params.status = status;
	}
	if (keywords) {
		params.keywords = keywords;
	}

	const { data } = await axiosInstance().get(baseUrl, { params });
	return data;
};

export const getDhcDetails = async (dhcId: string): Promise<DhcDetails> => {
	const { data } = await axiosInstance().get(`${baseUrl}/${dhcId}`);
	return data;
};

export const getDhcNotes = async (dhcId: string): Promise<DhcNotesResult> => {
	const { data } = await axiosInstance().get(`administration/dhc/notes/${dhcId}`);
	return data;
};

export const getDhcList = async (keywords?: string): Promise<DhcListResult> => {
	const params = keywords ? { keywords } : undefined;
	const { data } = await axiosInstance().get(`aggregator/v1/delivery/dhc/name-list`, { params });
	return data;
};

export const getDhcExpertiseList = async (): Promise<DhcExpertiseResult> => {
	const { data } = await axiosInstance().get(`${baseUrl}/expertise/list`);
	return data;
};

export const getDhcQaList = async (): Promise<DhcQaResult> => {
	const { data } = await axiosInstance().get(`aggregator/v1/delivery/qa`);
	return data;
};

export const configureDhc = async (payload: ConfigureDhcPayload): Promise<void> => {
	await axiosInstance().post(`administration/dhc/configure`, payload);
};

export const createDhcNote = async (dhcId: number, note: string): Promise<void> => {
	await axiosInstance().post(`administration/dhc/notes`, { note, dhcId });
};

export const deleteDhcNote = async (noteId: number): Promise<void> => {
	await axiosInstance().delete(`administration/dhc/notes/${noteId}`);
};

export const editDhcNote = async (noteId: number, note: string): Promise<void> => {
	await axiosInstance().patch(`administration/dhc/notes/edit`, { noteId, note });
};

export const getDhcClients = async (dhcId: string): Promise<DhcClientResult> => {
	const { data } = await axiosInstance().get(`${baseUrl}/${dhcId}/clients`);
	return data;
};

export const getDhcAdminClients = async (
	dhcId: string,
	page = 1,
	limit = 20,
	locale = "en"
): Promise<DhcAdminClientResult> => {
	const parsedId = Number(dhcId);
	const params = { page, limit, dhcId: Number.isNaN(parsedId) ? dhcId : parsedId, locale };
	const { data } = await axiosInstance().get(`aggregator/v1/delivery/dhc/clients`, { params });
	return data;
};

export const getDhcSwapAssignClients = async (
	dhcId: string,
	page = 1,
	limit = 20,
	locale = "en"
): Promise<DhcAdminClientResult> => {
	const parsedId = Number(dhcId);
	const params = { page, limit, dhcId: Number.isNaN(parsedId) ? dhcId : parsedId, locale };
	const { data } = await axiosInstance().get(`aggregator/v1/delivery/dhc/clients`, { params });
	return data;
};

export const getDhcOpenUsers = async (page = 1, limit = 20, locale = "en"): Promise<DhcOpenUsersResult> => {
	const params = { page, limit, locale };
	const { data } = await axiosInstance().get(`aggregator/v1/delivery/dhc/open-users`, { params });
	return data;
};

export const getDhcPayouts = async (dhcId: string): Promise<DhcPayouts> => {
	const { data } = await axiosInstance().get(`${baseUrl}/${dhcId}/payouts`);
	return data;
};

export const getDhcPayoutApproval = async (dhcIds: string[], locale = "en"): Promise<DhcPayoutApprovalResult> => {
	const { data } = await axiosInstance().get(`${baseUrl}/payout-approval`, {
		params: { dhcIds, locale },
		paramsSerializer: (params) => {
			const search = new URLSearchParams();
			Object.entries(params).forEach(([key, value]) => {
				if (Array.isArray(value)) {
					value.forEach((v) => search.append(key, String(v)));
				} else {
					search.append(key, String(value));
				}
			});
			return search.toString();
		},
	});
	return data;
};

export const batchRatePeriods = async (
	periods: { id: number; qaRating: number; qaComment: string }[]
): Promise<void> => {
	await axiosInstance().post(`administration/delivery-periods/batch-rate`, { periods });
};

export const ratePeriod = async (id: number, qaRating: number, qaComment: string): Promise<void> => {
	await axiosInstance().patch(`administration/delivery-periods/${id}/rate`, { qaRating, qaComment });
};

export const exportDhcs = async (
	format: "csv" | "xlsx",
	ids: Array<number | string>,
	signal?: AbortSignal
): Promise<Blob> => {
	const { data } = await axiosInstance().get(`${baseUrl}/export`, {
		params: { format, ids },
		paramsSerializer: (params) => {
			const search = new URLSearchParams();
			Object.entries(params).forEach(([key, value]) => {
				if (Array.isArray(value)) {
					value.forEach((v) => search.append(key, String(v)));
				} else {
					search.append(key, String(value));
				}
			});
			return search.toString();
		},
		responseType: "blob",
		signal,
		timeout: 120000,
	});
	return data;
};
